import type { ReactNode } from 'react';
import { Illustration, type IllustrationName } from './Illustration';

/**
 * The shell the two signed-out screens share.
 *
 * On a wide viewport it is a split panel — brand on the left, the form on the
 * right. Below `lg` the panel is dropped rather than stacked: someone signing
 * in on a phone wants the password field, not a full-height illustration above
 * it.
 */
export function AuthLayout({
  children,
  illustration,
  headline,
  blurb,
}: {
  children: ReactNode;
  illustration: IllustrationName;
  headline: string;
  blurb: string;
}) {
  return (
    <div className="grid min-h-screen bg-white lg:grid-cols-2">
      <div className="hidden flex-col bg-brand-800 p-10 text-white lg:flex">
        <div>
          <img src="/elixir-logo.png" alt="Elixir" width={169} height={56} className="h-9 w-auto" />
          {/* Peridot is a 1.31:1 disaster on white but reads cleanly on brand-800. */}
          <span className="mt-2 block text-sm font-semibold uppercase tracking-[0.2em] text-peridot-400">
            Finance Ops
          </span>
        </div>

        <div className="flex flex-1 flex-col items-center justify-center">
          <Illustration
            name={illustration}
            className="h-80 max-h-[50vh] xl:h-[26rem] 2xl:h-[34rem]"
            eager
          />
          <h2 className="mt-8 max-w-sm text-center text-2xl font-semibold tracking-tight">
            {headline}
          </h2>
          <span className="mt-5 h-1 w-12 rounded-full bg-peridot-500" />
          <p className="mt-5 max-w-sm text-center text-sm text-brand-100">{blurb}</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">{children}</div>
      </div>
    </div>
  );
}
